import { Button, Tooltip } from "@mantine/core";
import { IconWand } from "@tabler/icons-react";
import type { DateTime } from "luxon";
import type { MRT_ColumnDef } from "mantine-react-table-open";
import { useMeteringOrMarketLocationSuggestedCommissionPeriodMutation } from "../../../../hooks/useMeteringOrMarketLocationSuggestedCommissionPeriodMutation.ts";
import { HeaderWithHelpIcon } from "../../../../mantine/mrt/components/header/HeaderWithHelpIcon.tsx";
import { useToast } from "../../../../mantine/notifications/useToast.tsx";
import type { MeteringOrMarketLocation } from "../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { DATE_TIME_DISPLAY_FORMAT } from "../../../../utils/dates/dates.constants";
import type { ObjectName } from "../../../../utils/enums";
import type { ConfiguratorComponent } from "../ComponentTable";

function SuggestCommissionPeriodCell({
  locationId,
  objectName
}: {
  locationId: number;
  objectName: ObjectName.MeteringLocation | ObjectName.MarketLocation;
}) {
  const { showErrorToast } = useToast();
  const mutation =
    useMeteringOrMarketLocationSuggestedCommissionPeriodMutation(objectName);
  return (
    <Tooltip label="IBN/ABN automatisch setzen">
      <Button
        aria-label="IBN/ABN automatisch setzen"
        loading={mutation.isPending}
        size="xs"
        onClick={() =>
          mutation.mutate(locationId, {
            onError: (error) => showErrorToast(error)
          })
        }
      >
        <IconWand size={15} />
      </Button>
    </Tooltip>
  );
}

function getStaffLocationColumns(
  objectName: ObjectName.MeteringLocation | ObjectName.MarketLocation
): Array<MRT_ColumnDef<ConfiguratorComponent>> {
  return [
    { header: "Status", accessorKey: "status", size: 100 },
    { header: "Kommentar", accessorKey: "comment" },
    {
      header: "Änderungsdatum",
      id: "updated",
      accessorFn: (rowData) =>
        backendDateOrDateTimeToLuxonDateTime(
          (rowData as MeteringOrMarketLocation).updated
        ),
      Cell: ({ cell }) => {
        const value = cell.getValue<DateTime>();
        return value ? value.toFormat(DATE_TIME_DISPLAY_FORMAT) : null;
      }
    },
    {
      header: "IBN",
      Header: HeaderWithHelpIcon({
        headerText: "IBN",
        helpText:
          "Datum der Inbetriebnahme der Lokation wie mitgeteilt durch den Messstellenbetreiber. Wird gegebenenfalls durch node.energy nach Rückmeldung des Messstellenbetreibers ausgefüllt. Standardmäßig vorbefüllt mit der ältesten Inbetriebnahme der EE-Anlagen, die zu dieser Lokation gehören."
      }),
      accessorKey: "commissioning",
      size: 130
    },
    {
      header: "ABN",
      Header: HeaderWithHelpIcon({
        headerText: "ABN",
        helpText:
          "Datum der Außerbetriebnahme der Lokation wie mitgeteilt durch den Messstellenbetreiber. Wird gegebenenfalls durch node.energy nach Rückmeldung des Messstellenbetreibers ausgefüllt. Standardmäßig vorbefüllt mit der aktuellsten Außerbetriebnahme der EE-Anlagen, die zu dieser Lokation gehören."
      }),
      accessorKey: "decommissioning",
      size: 130
    },
    {
      header: "IBN/ABN Vorschlag",
      Header: HeaderWithHelpIcon({
        headerText: (
          <>
            IBN/ABN
            <br />
            Vorschlag
          </>
        ),
        helpText:
          "Setze die IBN/ABN der Lokation automatisch per Knopfdruck. Die gesetzten Zeitpunkte basieren auf" +
          " den Betriebszeiten aller EE-Anlagen, die wir einer Lokation über den Liegenschafts-Graphen und die" +
          " Erzeugungsanteile zuordnen können. Sobald eine der zugeordneten EE-Anlagen einen fehlenden Eintrag für" +
          " z. B. IBN hat, wird auch die IBN für die Lokation auf 'None' gesetzt. Wenn alle" +
          " zugeordneten EE-Anlagen ein IBN oder ABN Datum haben, wird die älteste IBN und die jüngste" +
          " ABN für die Lokation gesetzt."
      }),
      size: 120,
      Cell: ({ row }) => {
        const locationId = (row.original as MeteringOrMarketLocation).id;
        return (
          <SuggestCommissionPeriodCell
            locationId={locationId}
            objectName={objectName}
          />
        );
      }
    }
  ];
}

export { getStaffLocationColumns };
